<template>
  <div
    class="group relative flex flex-row items-center overflow-hidden p-3"
    :class="[isClickable && 'cursor-pointer']"
    @click="click"
  >
    <CheckableAvatar :member="member" :is-checked="state == OpenFeedbackState.Completed" />
    <span class="text-body-large ml-2 inline-flex items-center">
      {{ memberFullName(member) }}
    </span>
    <div class="ml-auto flex flex-row items-center">
      <span
        v-if="state == OpenFeedbackState.Pending"
        class="text-on-surface-variant inline-flex items-center"
      >
        <BaseIcon size="20" class="mr-1.5">hourglass</BaseIcon>
        <span class="text-label-large">Ausstehend</span>
      </span>
      <span
        v-if="state == OpenFeedbackState.InProgress"
        class="text-primary inline-flex items-center"
      >
        <BaseIcon size="20" class="mr-1.5">chat_bubble</BaseIcon>
        <span class="text-label-large">In Bearbeitung</span>
      </span>
      <span
        v-if="state == OpenFeedbackState.Rejected && rejectionReason"
        class="text-primary inline-flex items-center"
      >
        <BaseIcon size="20" class="mr-1.5">close</BaseIcon>
        <span class="text-label-large">Abgelehnt mit Begründung</span>
      </span>
      <span
        v-if="state == OpenFeedbackState.Rejected && !rejectionReason"
        class="text-primary inline-flex items-center"
      >
        <BaseIcon size="20" class="mr-1.5">close</BaseIcon>
        <span class="text-label-large">Abgelehnt ohne Begründung</span>
      </span>
      <span v-if="state == OpenFeedbackState.Completed" class="inline-flex items-center">
        <span class="text-label-large"
          >{{ numberOfFeedbacks }}/{{ totalNumberOfQuestions }}
          {{ $t('common.question', totalNumberOfQuestions) }}</span
        >
      </span>
    </div>
    <BaseIcon v-if="isClickable" class="ml-4">chevron_right</BaseIcon>
    <BaseStateLayer v-if="isClickable" />
    <BaseDialog
      v-if="rejectionReason && state == OpenFeedbackState.Rejected"
      :is-open="isOpen"
      @close="closeDialog"
      type="basic"
      :title="rejectionReason"
    >
      <template #dismiss
        ><BaseTextButton @click="closeDialog">{{ $t('common.close') }}</BaseTextButton>
      </template>
    </BaseDialog>
  </div>
</template>
<script setup lang="ts">
  import { useRouter } from 'vue-router'
  import BaseIcon from '../../../core/components/base/icon/BaseIcon.vue'
  import BaseStateLayer from '../../../core/components/base/state-layer/BaseStateLayer.vue'
  import type { Member } from '../../../core/models/member/member'
  import { OpenFeedbackState } from '../../open-feedbacks/composables/useOpenFeedbacks'
  import BaseDialog from '../../../core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { computed } from 'vue'
  import CheckableAvatar from '../../../core/components/base/avatar/CheckableAvatar.vue'
  import { memberFullName } from '@/core/models/member/member-name.ts'

  interface Props {
    member: Member
    numberOfFeedbacks: number
    state: OpenFeedbackState
    totalNumberOfQuestions: number
    surveyId: string
    rejectionReason?: string
  }

  const props = defineProps<Props>()

  const router = useRouter()

  const { isOpen, openDialog, closeDialog } = useDialog()

  const click = () => {
    if (props.state == OpenFeedbackState.Completed) {
      router.push({
        name: 'survey-member-feedback',
        params: { surveyId: props.surveyId, memberId: props.member.id },
      })
    }
    if (props.state == OpenFeedbackState.Rejected && props.rejectionReason) {
      openDialog()
    }
  }

  const isClickable = computed(() => {
    return (
      props.state == OpenFeedbackState.Completed ||
      (props.state == OpenFeedbackState.Rejected && props.rejectionReason)
    )
  })
</script>
